/**
 * v0.18.86 — 「我的卡片」**卡面 / 弹层共用的展示口径**
 *
 * ## 为什么单独抽一个文件 (本仓铁律「一个逻辑一个入口」)
 *   v0.18.86 一批同时改了**两处**会显示同一批事实的地方:
 *     · 卡面 (`MyCardsTab.js` 的 renderCard)   —— 状态签 / 有效期 / 课时文案
 *     · 弹层 (`MyCardDetailModal.js`)         —— 标题栏状态签 / 有效期 / 课次状态词
 *   两边各写一份 ⇒ 迟早分叉 (学员在卡面看到「生效中」、在弹层里看到「已过期」),
 *   而这种分叉**极难在测试里被发现** (两处断言各自都"对")。
 *   ⇒ 口径收在这里, 两边都 import。
 *
 * ## ⛔ 课次状态词: **不是**另造的一套
 *   逐字取自 `js/src/forum/components/LessonsTab.js:884-893` 的 `statusBadge()` map:
 *     scheduled = 已预约 · confirmed = 已确认 · checked-in = 已签到
 *     completed = 已消课 · cancelled = 已取消
 *   ⛔ **不许**出现「已过期」「待上课」这类现网不存在的状态 (设计稿里的那两个词已被裁决掉,
 *      过去的课按 scheduled / completed 显示即可)。
 */

/** 降级最高级。L2 已是兜底, 再降就要丢信息了 ⇒ 终止。 */
export const MC_LEVEL_MAX = 2;

/**
 * 距到期还有几天。按**日历天**算, 与后端 `expires_at` 落在当天 00:00 的口径对齐:
 * 今天到期 → 0 (已过期), 明天到期 → 1。
 *
 * ⚠️ **不用** `Math.ceil((t - now) / 86400000)`: 那是「剩余小时数向上取整」,
 *   晚上 23:59 看一张明天 00:00 到期的卡会算出 0 (= 已过期), 口径与后端打架。
 *   这里先把两边都归一到当地日历日再除, 结果是纯整数天。
 */
export function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  const parts = String(expiresAt).split('-');
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  const d = Number(parts[2]);
  if (!y || !mo || !d) return null;
  const today = new Date();
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((Date.UTC(y, mo - 1, d) - todayUtc) / 86400000);
}

/** YYYY-MM-DD → MM/DD (同 StudentsListTab.formatExpires 的短格式)。 */
export function shortDate(s) {
  const parts = String(s).split('-');
  return parts.length === 3 ? parts[1] + '/' + parts[2] : s;
}

/**
 * 有效期四档 (辉哥 10/17 拍板): >30天绿 / 8–30天橘黄 / 1–7天红 / 永久灰。
 *
 * 🔴 与 `StudentsListTab.formatExpires()` (js/.../StudentsListTab.js:462) **逐档冲突**。
 *   现有 formatExpires 只有**三档**, 阈值 / 配色 / 文案跟本表都不一样:
 *     | 天数   | formatExpires (现有)   | 本 tab (辉哥 10/17 拍板) |
 *     |--------|------------------------|------------------------|
 *     | 无到期 | null (前端当「不渲染」)  | 灰 /「永久有效」       |
 *     | >30    | green                  | 绿                      |
 *     | 8–30   | green ← ⚠️ 冲突        | 橘黄                    |
 *     | 1–7    | orange ← ⚠️ 冲突       | 红                      |
 *     | ≤0     | red「已过期 N 天」      | 红                      |
 *   任务书要求「三档阈值统一走现有 formatExpires()」**且**「若冲突不要改它」——
 *   两条互斥 (真冲突时无法既统一走它又不改它)。
 *   ⇒ 本文件的取舍: **一个字都不改** formatExpires(它服务「学员列表」tab, 改它是另一个 tab 的回归),
 *     本 tab 按**辉哥 10/17 拍板的四档**实现, 冲突已写进 REPORT.md 等辉哥定。
 *   **口径真正复用**的部分: 「今天到期 = 已过期」与 `StudentCard::isExpired()` 同源 ——
 *     后端已经把 `is_expired` 算好返给前端, 前端**不再自己重算**这个布尔。
 *   文案: 1–30 天一律「还剩N天」(辉哥 10/17 拍板 3.2: 「还剩1天」就写「还剩1天」,
 *   不写设计稿 v7 的「1 天后到期」); >30 天沿用 formatExpires 的「MM/DD 到期」短格式。
 */
export function expTier(card) {
  // 永久有效 —— 灰
  if (!card.expires_at) {
    return {
      cls: 'ZS-mc-t-gray',
      text: '永久有效'
    };
  }
  const days = daysLeft(card.expires_at);

  // 🔴 「今天到期」按 isExpired() 的 isPast() 语义判**已过期**, 不是未过期
  //   (expires_at 的 cast 是 'date:Y-m-d' ⇒ Carbon 落在当天 00:00:00 ⇒ 当天就已 isPast())。
  if (days === null || days <= 0) {
    if (days === 0) return {
      cls: 'ZS-mc-t-red',
      text: '今天到期'
    };
    return {
      cls: 'ZS-mc-t-red',
      text: '已过期 ' + Math.abs(days) + ' 天'
    };
  }

  // 1–7天 红 —— ⚠️ 红字是辉哥知情拍板的妥协, 不要改成 AA 合规色
  if (days <= 7) return {
    cls: 'ZS-mc-t-red',
    text: '还剩' + days + '天'
  };
  // 8–30天 橘黄
  if (days <= 30) return {
    cls: 'ZS-mc-t-amber',
    text: '还剩' + days + '天'
  };
  // >30天 绿
  return {
    cls: 'ZS-mc-t-green',
    text: shortDate(card.expires_at) + ' 到期'
  };
}

/**
 * 状态签四态 (辉哥 10/17 拍板): 未激活 / 生效中 / 已过期 / 课时用完。
 *
 * ⚠️ 每个条件**独立**判定, 不是「else if」—— 「未激活 + 已过期」是**双签**,
 *   「课时用完 + 今天到期」也是双签, 少判一个学员就少知道一条。
 * ⚠️ 入队顺序 = 优先级: 未激活 > 已过期 > 课时用完 > 生效中。
 *   L1 降级时 `slice(0,1)` 那个是主签 (留在右缘), 其余挪到有效期那一行。
 */
export function statusesOf(card) {
  const all = [];
  const dl = daysLeft(card.expires_at);
  const expired = card.is_expired === true || dl !== null && dl <= 0;
  if (!card.is_activated) all.push({
    k: 'st-locked',
    label: '未激活',
    icon: 'fa-lock'
  });
  if (expired) all.push({
    k: 'st-expired',
    label: '已过期',
    icon: 'fa-circle-info'
  });
  if (!card.is_unlimited && Number(card.remaining_lessons) <= 0) {
    all.push({
      k: 'st-usedup',
      label: '课时用完',
      icon: 'fa-circle-exclamation'
    });
  }
  if (all.length === 0) all.push({
    k: 'st-active',
    label: '生效中',
    icon: 'fa-circle-check'
  });
  return all;
}

/**
 * 课时文案: 无限次卡显示「不限次」, 否则卡面「15 / 15」。
 * ⚠️ **不展示 consumed_count** (辉哥 10/17 拍板 3.4)。
 */
export function lessonsText(card) {
  if (card.is_unlimited) return '不限次';
  return trimNum(card.remaining_lessons) + ' / ' + trimNum(card.total_lessons);
}

/** DECIMAL(10,1) 后端返的是 float; 9.0 该显示 9 而不是 9.0。 */
export function trimNum(n) {
  if (n === null || n === undefined) return '0';
  const f = Number(n);
  if (!isFinite(f)) return '0';
  return String(Math.round(f * 10) / 10);
}

/**
 * 课次状态 → { label, class }
 *
 * ⛔ 词表**逐字**取自 LessonsTab.js:884-893 的 statusBadge() map (见文件头注释)。
 *   ⛔ 不许新增「已过期」「待上课」这类现网不存在的状态。
 */
export function lessonStatus(status) {
  const map = {
    scheduled: {
      label: '已预约',
      class: 'scheduled'
    },
    confirmed: {
      label: '已确认',
      class: 'confirmed'
    },
    'checked-in': {
      label: '已签到',
      class: 'checked-in'
    },
    completed: {
      label: '已消课',
      class: 'completed'
    },
    cancelled: {
      label: '已取消',
      class: 'cancelled'
    }
  };
  return map[status] || {
    label: status || '',
    class: 'scheduled'
  };
}

/**
 * 上课日期 → 「10/07」+「周三」。
 *
 * ⚠️ 星期用 `new Date(...)` 的**本地**分量算 —— `toISOString()` 是 UTC,
 *   在东八区会把周三算成周二 (同 LessonsTab.js 的 fmtLocalDate 纪律)。
 *
 * ⚠️ 这里收**原始字符串** (lesson.start_at) 而不是整个 lesson 对象:
 *   两个消费方 (卡片详情弹层 / 自主约课弹窗) 传进来的东西形状不同,
 *   收字符串才是**一个**入口能同时服务两边 (本仓铁律「一个逻辑一个入口」)。
 */
export function lessonDateParts(raw) {
  const s = String(raw || '');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return {
    d1: '',
    d2: ''
  };
  const wd = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  let d2 = '';
  try {
    const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d2 = wd[dt.getDay()];
  } catch (e) {
    d2 = '';
  }
  return {
    d1: m[2] + '/' + m[3],
    d2: d2
  };
}

/** 课次时段 → 「19:00 – 20:00」 (缺 end_time 时只显示 start_time)。 */
export function lessonTimeText(lesson) {
  const s = lesson && lesson.start_time || '';
  const e = lesson && lesson.end_time || '';
  if (!s) return '';
  if (!e) return s;
  return s + ' – ' + e;
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/MyCardView', { MC_LEVEL_MAX: MC_LEVEL_MAX,daysLeft: daysLeft,shortDate: shortDate,expTier: expTier,statusesOf: statusesOf,lessonsText: lessonsText,trimNum: trimNum,lessonStatus: lessonStatus,lessonDateParts: lessonDateParts,lessonTimeText: lessonTimeText, });